
import { Options, Vue } from "vue-class-component";
import { animateCountUp } from "@/utils/countUp";
import { formatDashboardAmount } from "@/utils/homeDashboardMetrics";

function toSafeNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

@Options({
  props: {
    count: { type: Number, default: 0 },
    format: { type: String, default: "number" },
    duration: { type: Number, default: 900 },
  },
  watch: {
    count(newCount: number) {
      (this as AnimatedCounter).animateTo(newCount);
    },
  },
})
export default class AnimatedCounter extends Vue {
  declare readonly count: number;
  declare readonly format: "number" | "currency";
  declare readonly duration: number;

  displayValue = "0";
  private cancelAnimation: (() => void) | null = null;
  private currentNumericValue = 0;

  mounted() {
    this.animateTo(this.count);
  }

  beforeUnmount() {
    this.cancelAnimation?.();
  }

  formatDisplay(value: number): string {
    const safeValue = toSafeNumber(value);
    return this.format === "currency"
      ? formatDashboardAmount(Math.round(safeValue))
      : Math.round(safeValue).toLocaleString("fr-FR");
  }

  animateTo(rawTarget: unknown) {
    const target = toSafeNumber(rawTarget);
    this.cancelAnimation?.();

    if (this.currentNumericValue === target) {
      this.displayValue = this.formatDisplay(target);
      return;
    }

    const from = this.currentNumericValue;

    this.cancelAnimation = animateCountUp(
      from,
      target,
      this.duration,
      (value) => {
        this.currentNumericValue = toSafeNumber(value);
        this.displayValue = this.formatDisplay(this.currentNumericValue);
      },
      () => {
        this.currentNumericValue = target;
        this.displayValue = this.formatDisplay(target);
      }
    );
  }
}
